import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Settings pages have no Save button — a change saves itself.
 * `trigger(value)` debounces, calls `save(value)` and reports status for the
 * little "Saving…/Saved" marker. The last call inside the window wins.
 */
export function useAutosave(save, { delay = 700 } = {}) {
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const timer = useRef(null);
  const latest = useRef(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  // A pending change must not be lost when the component unmounts or the tab is closed.
  useEffect(() => {
    const flushPending = () => {
      if (!timer.current) return;
      clearTimeout(timer.current);
      timer.current = null;
      Promise.resolve(saveRef.current(latest.current)).catch(() => {});
    };
    window.addEventListener("pagehide", flushPending);
    return () => {
      window.removeEventListener("pagehide", flushPending);
      flushPending();
    };
  }, []);

  const run = useCallback(async () => {
    setStatus("saving");
    try {
      await saveRef.current(latest.current);
      setStatus("saved");
    } catch (e) {
      setStatus("error");
      throw e;
    }
  }, []);

  const trigger = useCallback((value) => {
    latest.current = value;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      run().catch(() => {});
    }, delay);
  }, [delay, run]);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    return run().catch(() => {});
  }, [run]);

  return { status, trigger, flush };
}

export function AutosaveStatus({ status, className = "" }) {
  const text = { saving: "Saving…", saved: "Saved", error: "Not saved" }[status];
  if (!text) return null;
  return (
    <span data-testid="autosave-status"
          className={`text-xs ${status === "error" ? "text-red-600" : "text-slate-400"} ${className}`}>
      {text}
    </span>
  );
}
